import { useState, useEffect } from 'react';
import { getPhrasesByCategory, getRandomPhrase, getTodaysPhrase, type MotivationalPhrase } from './fetchGetPhrases';

// Hook para obtener la frase del día
export function useTodaysPhrase() {
  const [phrase, setPhrase] = useState<MotivationalPhrase | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchTodaysPhrase = async () => {
      try {
        setLoading(true);
        setError(null);
        const todaysPhrase = await getTodaysPhrase();
        setPhrase(todaysPhrase);
      } catch (err) {
        setError('Error al cargar la frase del día');
        console.error('Error fetching today\'s phrase:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchTodaysPhrase();
  }, []);

  const refreshPhrase = async () => {
    try {
      setLoading(true);
      const newPhrase = await getTodaysPhrase();
      setPhrase(newPhrase);
    } catch (err) {
      setError('Error al actualizar la frase');
    } finally {
      setLoading(false);
    }
  };

  return { phrase, loading, error, refreshPhrase };
}

// Hook para obtener frases por categoría
export function usePhrasesByCategory(category: string | null) {
  const [phrases, setPhrases] = useState<MotivationalPhrase[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!category) return;

    const fetchPhrases = async () => {
      try {
        setLoading(true);
        setError(null);
        const categoryPhrases = await getPhrasesByCategory(category);
        setPhrases(categoryPhrases);
      } catch (err) {
        setError('Error al cargar las frases de la categoría');
        console.error('Error fetching phrases by category:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchPhrases();
  }, [category]);

  return { phrases, loading, error };
}

// Hook para obtener todas las categorías
export function useCategories() {
  const [phrase, setPhrase] = useState<MotivationalPhrase | null>(null);
    const [lastFetchDate, setLastFetchDate] = useState<string | null>(null);
  const [categories, _setCategories] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchTodaysPhrase = async () => {
      // Cache: solo buscar si es un nuevo día
      const today = new Date().toDateString();
      if (lastFetchDate === today && phrase) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const todaysPhrase = await getTodaysPhrase();
        setPhrase(todaysPhrase);
        setLastFetchDate(today);
      } catch (err) {
        setError('Error al cargar la frase del día');
        console.error('Error fetching today\'s phrase:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchTodaysPhrase();
  }, [lastFetchDate, phrase]);

  return { categories, loading, error };
}

// Hook para frases aleatorias
export function useRandomPhrase() {
  const [phrase, setPhrase] = useState<MotivationalPhrase | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getNewRandomPhrase = async () => {
    try {
      setLoading(true);
      setError(null);
      const randomPhrase = await getRandomPhrase();
      setPhrase(randomPhrase);
    } catch (err) {
      setError('Error al cargar la frase aleatoria');
      console.error('Error fetching random phrase:', err);
    } finally {
      setLoading(false);
    }
  };

  return { phrase, loading, error, getNewRandomPhrase };
}